import React, { useState, useContext, useEffect } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'babel-plugin-relay/macro';
import { Container, Row, Col } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import './Header.sass';

import HeaderThemeContext from '../../context/HeaderThemeContext';

import SecondLevelNav from '../SecondLevelNav';
import MobileNav from '../MobileNav';

import Bubble from '../../uikit/Bubble';
import LinkButton from '../../uikit/Links/LinkButton';

import { ReactComponent as LogoWhite } from './it-nonstop-logo-white.svg';
import { ReactComponent as LogoColor } from './it-nonstop-logo-color.svg';
import { ReactComponent as TwitterIcon } from '../../uikit/Icons/twitter-logo-light.svg';
import { ReactComponent as FacebookIcon } from '../../uikit/Icons/facebook-logo-light.svg';
import { ReactComponent as Burger } from './burger.svg';

interface Props {
	buttonLabel: string;
	// query?: any;
}

interface IData {
	comingSoonAbout: boolean;
	comingSoonAgenda: boolean;
	comingSoonSpeakers: boolean;
	comingSoonPartners: boolean;
	comingSoonFAQ: boolean;
}

function Header(props: Props) {
	const [isDark] = useContext(HeaderThemeContext);
	const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
	const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(false);
	const location = useLocation();

	// const data: IData = useFragment(
	// 	graphql`
	// 		fragment Header_query on HomePublishedContent {
	// 			comingSoonAbout
	// 			comingSoonAgenda
	// 			comingSoonSpeakers
	// 			comingSoonPartners
	// 			comingSoonFAQ
	// 		}
	// 	`,
	// 	props.query
	// );

	const data = {
		comingSoonAbout: true,
		comingSoonAgenda: true,
		comingSoonSpeakers: true,
		comingSoonPartners: true,
		comingSoonFAQ: true,
	}

	const switchDarkClass = () => {
		const scrollpos = window.scrollY;
		const triggerPos = document.querySelector('.hero-banner')?.clientHeight;
		const header = document.querySelector('.header');
		if (header && !isDesktopNavOpen) {
			if (triggerPos && scrollpos > triggerPos) {
				header.classList.remove('header--dark');
			} else {
				header.classList.add('header--dark');
			}
		}
	};
	const switchShadowClass = () => {
		const scrollpos = window.scrollY;
		const header = document.querySelector('.header');
		if (header) {
			if (scrollpos > 10) {
				header.classList.add('header--shadow');
			} else {
				header.classList.remove('header--shadow');
			}
		}
	};

	useEffect(() => {
		setTimeout(() => {
			if (window.scrollY === 0) {
				document.querySelector('.header')?.classList.remove('header--shadow');
			}
		}, 300);
		window.addEventListener('scroll', switchShadowClass);
		return () => {
			window.removeEventListener('scroll', switchShadowClass);
		};
	}, [location]);

	useEffect(() => {
		document.body.classList.remove('overflow-hidden');
		setIsMobileNavOpen(false);
		setIsDesktopNavOpen(false);
		window.scrollTo(0, 0);
	}, [location]);

	useEffect(() => {
		if (isDark && !isDesktopNavOpen) {
			switchDarkClass();
			window.addEventListener('scroll', switchDarkClass);
		}
		return () => {
			window.removeEventListener('scroll', switchDarkClass);
		};
	}, [isDark, isDesktopNavOpen]);

	return (
		<header
			className={`header header--shadow header--fixed${
				// eslint-disable-next-line no-nested-ternary
				isDesktopNavOpen ? ' header--white' : isDark ? ' header--dark' : ''
			}`}
		>
			<SecondLevelNav isOpen={isDesktopNavOpen} />

			<div className="header__main">
				{/* Remove .header--dark to make white header */}
				{/* Probably you should remove class .header--dark on nav item with second-level pages */}
				{/* If you remove .header--fixed header will become absolute */}
				<Container>
					<Row className="header__inner">
						<Col xs={6} lg={2} className="header__logo">
							<NavLink to="/">
								<LogoWhite />
								<LogoColor />
							</NavLink>
						</Col>
						<Col xs={6} lg={7} className="header__nav header-nav">
							<nav>
								{/* Use .header-nav__link--active for active links */}
								{data?.comingSoonAbout ? (
									<span
										className="header-nav__link"
										onClick={() => setIsDesktopNavOpen(!isDesktopNavOpen)}
										onKeyPress={() => setIsDesktopNavOpen(!isDesktopNavOpen)}
										role="button"
										tabIndex={0}
										aria-label="button"
									>
										<span>About</span>
									</span>
								) : (
									<span className="header-nav__link header-nav__link--disabled">
										<span>About</span>
										<Bubble align="left-top" text="Coming Soon!" />
									</span>
								)}
								{data?.comingSoonAgenda ? (
									<NavLink
										className="header-nav__link"
										to="/agenda"
										activeClassName="header-nav__link--active"
									>
										<span>Agenda</span>
									</NavLink>
								) : (
									<span className="header-nav__link header-nav__link--disabled">
										<span>Agenda</span>
										<Bubble align="left-top" text="Coming Soon!" />
									</span>
								)}
								{data?.comingSoonSpeakers ? (
									<NavLink
										className="header-nav__link"
										to="/speakers"
										activeClassName="header-nav__link--active"
									>
										<span>Speakers</span>
									</NavLink>
								) : (
									<span className="header-nav__link header-nav__link--disabled">
										<span>Speakers</span>
										<Bubble align="left-top" text="Coming Soon!" />
									</span>
								)}
								{data?.comingSoonPartners ? (
									<NavLink
										className="header-nav__link"
										to="/partners"
										activeClassName="header-nav__link--active"
									>
										<span>Partners</span>
									</NavLink>
								) : (
									<span className="header-nav__link header-nav__link--disabled">
										<span>Partners</span>
										<Bubble align="left-top" text="Coming Soon!" />
									</span>
								)}
								{data?.comingSoonFAQ ? (
									<NavLink
										className="header-nav__link"
										to="/faq"
										activeClassName="header-nav__link--active"
									>
										<span>FAQ</span>
									</NavLink>
								) : (
									<span className="header-nav__link header-nav__link--disabled">
										<span>FAQ</span>
										<Bubble align="left-top" text="Coming Soon!" />
									</span>
								)}
							</nav>
						</Col>
						<Col xs={6} lg={3} className="header__ctrls">
							<div className="header__socials header-socials">
								<a
									href=" https://twitter.com/it_nonstop"
									className="header-socials__link"
									target="_blank"
								>
									<TwitterIcon />
								</a>
								<a
									href="https://www.facebook.com/itnonstop.conference"
									className="header-socials__link"
									target="_blank"
								>
									<FacebookIcon />
								</a>
							</div>
							{/* Need help with prop for orange color for this button. Class .uikit-link-button--orange */}
							<LinkButton
								id="register_btn"
								label={props.buttonLabel}
								hrefSource="/register-to-the-conference"
								isOrange
								isExternal={false}
							/>
						</Col>
						<Col xs={6} className="header__burger">
							<button
								type="button"
								className="header__open-nav"
								onClick={() => {
									setIsMobileNavOpen(true);
									document.body.classList.add('overflow-hidden');
								}}
							>
								<Burger />
							</button>
							<MobileNav
								data={data}
								isOpen={isMobileNavOpen}
								onClick={() => {
									setIsMobileNavOpen(false);
									document.body.classList.remove('overflow-hidden');
								}}
							/>
						</Col>
					</Row>
				</Container>
			</div>
		</header>
	);
}

export default Header;
